import React, { useState, useRef, useContext } from 'react'
import styles from './index.module.css'
import { cn } from '@s4l/utility-css'
import { FormContext } from '../context'
import type { Rule } from '../Validations'
import { Typography } from '../../../index'
import useInputValue from '../hooks/useInputValue'

interface ITextAreaProps {
  name: string
  defaultValue?: string
  onChange?: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  validations?: Array<ReturnType<Rule>>
}

const TextArea = ({
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  name,
  validations,
  placeholder,
}: ITextAreaProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [value, setValue] = useState(defaultValue)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const { errors } = useContext(FormContext)

  const { onInputChange } = useInputValue({
    name,
    defaultValue,
    falseValue: '',
    validations,
    setInputValue: newValue => {
      if (typeof newValue === 'string') {
        setValue(newValue)
      }
    },
  })

  const handleFocus = () => {
    setIsFocused(true)
    onFocus?.()
  }

  const handleBlur = () => {
    setIsFocused(false)
    onBlur?.()
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    onInputChange(newValue)
    setValue(newValue)
    onChange?.(newValue)
  }

  const hasError = !!errors[name]

  return (
    <>
      <textarea
        name={name}
        ref={textAreaRef}
        placeholder={placeholder}
        className={cn(styles.textarea, {
          [styles.focused]: isFocused,
          [styles.hasError]: hasError,
          'has-content': (value?.length ?? 0) > 0,
        })}
        data-state-error={hasError}
        value={value ?? ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
      />
      {hasError && (
        <Typography variant="caption" size="l" color="var(--color-error-500)">
          {errors[name]}
        </Typography>
      )}
    </>
  )
}

TextArea.displayName = 'Form.TextArea'
export default TextArea
